'use client';

import type { LegalDocument } from "@/content/legal";
import { TallerButtonLink } from "@/components/taller/button";
import { useTranslations } from "@/i18n/client";

interface LegalPageLayoutProps {
  doc: LegalDocument;
  overline?: string;
}

/**
 * Reusable layout for legal pages.
 * Renders a LegalDocument with consistent taller editorial styling.
 */
export function LegalPageLayout({
  doc,
  overline,
}: LegalPageLayoutProps) {
  const { t, isLoading } = useTranslations();
  const leg = t('legal');

  if (isLoading) return null;

  return (
    <article className="max-w-[var(--container-max)] mx-auto px-6 md:px-14 py-20 md:py-28">
      {/* Page header */}
      <div className="max-w-[800px] mx-auto">
        <div className="text-[11px] font-mono tracking-widest uppercase text-[var(--color-mute)] mb-5">
          {overline || leg.overline}
        </div>

        {/* Intro */}
        <div className="mt-8 pb-10 border-b border-[color:var(--color-border)]">
          {doc.intro.map((paragraph, i) => (
            <p
              key={i}
              className="font-sans text-[16px] leading-[1.65] text-[color:var(--color-mute)] mt-0 mb-4 last:mb-0"
            >
              {paragraph}
            </p>
          ))}
        </div>

        {/* Table of contents */}
        <nav
          aria-label={leg.toc}
          className="mt-10 mb-12 bg-[color:var(--color-surface)] border border-[color:var(--color-border)] rounded-md px-7 py-6"
        >
          <div className="font-mono text-[10px] tracking-[0.18em] uppercase text-[color:var(--color-mute)] mb-4">
            {leg.toc}
          </div>
          <ol className="list-none p-0 m-0 space-y-2">
            {doc.sections.map((section) => (
              <li key={section.num}>
                <a
                  href={`#seccion-${section.num}`}
                  className="flex gap-3 items-baseline font-sans text-[14px] text-[color:var(--color-ink)] no-underline hover:text-[color:var(--color-accent)] transition-colors group"
                >
                  <span className="font-mono text-[11px] text-[color:var(--color-accent)] shrink-0 tracking-[0.1em] group-hover:opacity-70">
                    {section.num}.
                  </span>
                  {section.title}
                </a>
              </li>
            ))}
          </ol>
        </nav>

        {/* Sections */}
        <div className="space-y-14">
          {doc.sections.map((section) => (
            <section
              key={section.num}
              id={`seccion-${section.num}`}
              className="scroll-mt-8"
            >
              <div className="flex items-baseline gap-3 mb-5">

                <h2 className="font-sans font-[350] text-[26px] md:text-[30px] leading-[1.1] tracking-[-0.015em] text-[color:var(--color-ink)] m-0">
                  {section.title}
                </h2>
              </div>
              <div
                className="pl-8 border-l-2 border-[color:var(--color-border)] space-y-3"
              >
                {section.body.map((paragraph, j) => (
                  <p
                    key={j}
                    className="font-sans text-[15px] leading-[1.7] text-[color:var(--color-ink)] m-0"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-16 pt-10 border-t border-[color:var(--color-border)] flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-[color:var(--color-mute)] m-0">
            {leg.updated}: {doc.updated}
          </p>
          <TallerButtonLink variant="ghost" href="/contacto">
            {leg.questions}
          </TallerButtonLink>
        </div>
      </div>
    </article>
  );
}
